import { isThread, type ChannelDto } from '@lagerfeuer/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowLeft, Lock, LockKeyhole, MessagesSquare, MoreHorizontal, Users, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Menu, anchorFrom, type Anchor, type MenuItem } from '../../components/Popover';
import { Tooltip } from '../../components/Tooltip';
import { errorMessage } from '../../lib/api';
import { channelsApi, threadsApi } from '../../lib/endpoints';
import { isId, safeId } from '../../lib/ids';
import { qk } from '../../lib/queryClient';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import { displayName, useChannelPermissions } from '../../store/selectors';
import { toast } from '../../store/ui';
import { ForumPostHead } from '../forum/ForumPostHead';
import { TagPicker } from '../forum/TagPicker';
import { MessagePane } from '../messages/MessagePane';
import { threadAccess } from './threadUtils';
import './threads.css';

/** Thread aus dem Store bzw. nachladen (Link, Benachrichtigung). undefined = lädt, null = nicht gefunden. */
function useThreadChannel(threadId: string): ChannelDto | null | undefined {
  const ch = useAppStore((s) => s.channels[threadId]);
  const ready = useAppStore((s) => s.ready);
  const [missingId, setMissingId] = useState<string | null>(null);
  const valid = isId(threadId);
  const missing = !valid || missingId === threadId;
  useEffect(() => {
    if (ch || !ready || !valid) return;
    let alive = true;
    channelsApi
      .get(threadId)
      .then((c) => {
        if (!alive) return;
        if (isThread(c.type)) useAppStore.getState().dispatch('THREAD_UPDATE', c);
        else setMissingId(threadId);
      })
      .catch(() => alive && setMissingId(threadId));
    return () => {
      alive = false;
    };
  }, [threadId, ch, ready, valid]);
  if (ch) return isThread(ch.type) ? ch : null;
  return missing ? null : undefined;
}

/** Seitenpanel eines Threads bzw. Forenbeitrags (`?thread=<id>`), mobil als Vollbild. */
export function ThreadPanel({ threadId, onClose }: { threadId: string; onClose: () => void }) {
  const thread = useThreadChannel(threadId);
  if (thread === undefined) {
    return (
      <aside className="side-panel thread-panel" data-testid="thread-panel" data-thread-id={threadId} aria-busy="true">
        <PanelBar title="Thread" onClose={onClose} />
        <div className="empty full">
          <div className="spinner" />
        </div>
      </aside>
    );
  }
  if (thread === null) {
    return (
      <aside className="side-panel thread-panel" data-testid="thread-panel" data-thread-id={threadId}>
        <PanelBar title="Thread" onClose={onClose} />
        <div className="empty full" data-testid="thread-missing">
          <p>Thread nicht gefunden oder keine Berechtigung.</p>
        </div>
      </aside>
    );
  }
  return <ThreadPanelInner thread={thread} onClose={onClose} />;
}

function PanelBar({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <header className="thread-panel-header">
      <MessagesSquare size={20} aria-hidden="true" />
      <h2 className="thread-panel-title truncate">{title}</h2>
      <span className="spacer" />
      <button className="icon-btn" onClick={onClose} aria-label="Thread schließen" data-testid="thread-close">
        <X size={20} />
      </button>
    </header>
  );
}

type DialogState = { kind: 'rename' } | { kind: 'tags' } | { kind: 'delete' } | null;

function ThreadPanelInner({ thread, onClose }: { thread: ChannelDto; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const jumpId = safeId(params.get('message'));
  const meId = useAppStore((s) => s.me?.id);
  const parent = useAppStore((s) => (thread.parentId ? s.channels[thread.parentId] : undefined));
  const joinedLocal = useAppStore((s) => s.joinedThreadIds.includes(thread.id));
  const parentPerms = useChannelPermissions(thread.parentId ?? undefined);
  const access = threadAccess(thread, parentPerms, meId);
  const [menu, setMenu] = useState<Anchor | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [showMembers, setShowMembers] = useState(false);
  const [busy, setBusy] = useState(false);
  const inForum = parent?.type === 'FORUM';
  const meta = thread.thread;
  const archived = !!meta?.archived;
  const locked = !!meta?.locked;

  const members = useQuery({ queryKey: qk.threadMembers(thread.id), queryFn: () => threadsApi.members(thread.id), staleTime: 60_000 });
  const joined = members.data ? members.data.some((u) => u.id === meId) : joinedLocal;

  const applyUpdate = (dto: ChannelDto) => useAppStore.getState().dispatch('THREAD_UPDATE', dto);

  const patch = async (body: Parameters<typeof threadsApi.update>[1], okText?: string) => {
    setBusy(true);
    try {
      applyUpdate(await threadsApi.update(thread.id, body));
      if (okText) toast.success(okText);
      return true;
    } catch (e) {
      toast.error('Änderung fehlgeschlagen', errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const setMembership = async (join: boolean) => {
    setBusy(true);
    try {
      if (join) await threadsApi.join(thread.id);
      else await threadsApi.leave(thread.id);
      useAppStore.getState().update((d) => {
        d.joinedThreadIds = d.joinedThreadIds.filter((id) => id !== thread.id);
        if (join) d.joinedThreadIds.push(thread.id);
      });
      await queryClient.invalidateQueries({ queryKey: qk.threadMembers(thread.id) });
    } catch (e) {
      toast.error(join ? 'Beitreten fehlgeschlagen' : 'Verlassen fehlgeschlagen', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await threadsApi.remove(thread.id);
      useAppStore.getState().dispatch('THREAD_DELETE', { id: thread.id, parentId: thread.parentId ?? '', serverId: thread.serverId ?? '' });
      void queryClient.invalidateQueries({ queryKey: ['threads', thread.parentId] });
      toast.success(inForum ? 'Beitrag gelöscht' : 'Thread gelöscht');
      onClose();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen', errorMessage(e));
      setBusy(false);
    }
  };

  const menuItems: MenuItem[] = [
    { id: 'rename', label: inForum ? 'Titel ändern' : 'Umbenennen', onSelect: () => setDialog({ kind: 'rename' }), hidden: !access.canRename },
    { id: 'tags', label: 'Tags bearbeiten', onSelect: () => setDialog({ kind: 'tags' }), hidden: !inForum || !access.canEditTags || !parent?.availableTags?.length },
    {
      id: 'archive',
      label: archived ? 'Archivierung aufheben' : 'Archivieren',
      onSelect: () => void patch({ archived: !archived }, archived ? 'Archivierung aufgehoben' : 'Archiviert'),
      hidden: !access.canArchive,
    },
    { id: 'lock', label: locked ? 'Entsperren' : 'Sperren', onSelect: () => void patch({ locked: !locked }, locked ? 'Entsperrt' : 'Gesperrt'), hidden: !access.canLock },
    { id: 'leave', label: 'Thread verlassen', onSelect: () => void setMembership(false), hidden: !joined, separatorBefore: true },
    { id: 'delete', label: inForum ? 'Beitrag löschen' : 'Thread löschen', danger: true, onSelect: () => setDialog({ kind: 'delete' }), hidden: !access.canDelete, separatorBefore: true },
  ];
  const hasMenu = menuItems.some((i) => !i.hidden);
  const name = thread.name ?? 'Thread';
  const parentLabel = parent?.name ? (inForum ? `Forum ${parent.name}` : `#${parent.name}`) : null;

  return (
    <aside
      className="side-panel thread-panel"
      data-testid="thread-panel"
      data-thread-id={thread.id}
      data-archived={archived ? 'true' : undefined}
      data-locked={locked ? 'true' : undefined}
      aria-label={`${inForum ? 'Beitrag' : 'Thread'}: ${name}`}
    >
      <header className="thread-panel-header">
        <button className="icon-btn mobile-only" onClick={onClose} aria-label="Zurück zum Kanal" data-testid="thread-back">
          <ArrowLeft size={20} />
        </button>
        {thread.type === 'PRIVATE_THREAD' ? (
          <LockKeyhole size={20} aria-label="Privater Thread" role="img" />
        ) : (
          <MessagesSquare size={20} aria-hidden="true" />
        )}
        <div className="thread-panel-heading">
          <h2 className="thread-panel-title truncate" data-testid="thread-name" title={name}>
            {name}
          </h2>
          {parentLabel && <span className="thread-panel-sub truncate">{parentLabel}</span>}
        </div>
        {archived && (
          <span className="thread-flag" data-testid="thread-archived-badge" title="Archiviert">
            <Archive size={14} aria-hidden="true" /> Archiviert
          </span>
        )}
        {locked && (
          <span className="thread-flag" data-testid="thread-locked-badge" title="Gesperrt">
            <Lock size={14} aria-hidden="true" /> Gesperrt
          </span>
        )}
        <span className="spacer" />
        <Tooltip label="Mitglieder">
          <button
            className="icon-btn thread-member-count"
            onClick={() => setShowMembers(!showMembers)}
            aria-expanded={showMembers}
            aria-label={`${meta?.memberCount ?? 0} Mitglieder`}
            data-testid="thread-members-toggle"
          >
            <Users size={18} />
            <span>{meta?.memberCount ?? 0}</span>
          </button>
        </Tooltip>
        {joined ? null : (
          <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => void setMembership(true)} data-testid="thread-join">
            Beitreten
          </button>
        )}
        {hasMenu && (
          <Tooltip label="Einstellungen">
            <button className="icon-btn" onClick={(e) => setMenu(anchorFrom(e.currentTarget))} aria-haspopup="menu" aria-expanded={!!menu} data-testid="thread-settings">
              <MoreHorizontal size={20} />
            </button>
          </Tooltip>
        )}
        <Tooltip label="Schließen">
          <button className="icon-btn" onClick={onClose} aria-label="Thread schließen" data-testid="thread-close">
            <X size={20} />
          </button>
        </Tooltip>
      </header>

      {showMembers && (
        <section className="thread-members" aria-label="Thread-Mitglieder" data-testid="thread-members">
          {members.isLoading && <div className="spinner" />}
          {members.isError && <p className="small error-text">Mitglieder konnten nicht geladen werden.</p>}
          <ul role="list">
            {members.data?.map((u) => (
              <li key={u.id}>
                <button
                  className="thread-member"
                  onClick={(e) => overlays.profile(u.id, anchorFrom(e.currentTarget), thread.serverId)}
                  data-testid="thread-member"
                  data-user-id={u.id}
                >
                  <Avatar user={u} size={24} showStatus />
                  <span className="truncate">{displayName(u)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {inForum && parent && <ForumPostHead thread={thread} forum={parent} />}

      {(archived || locked) && (
        <div className="thread-banner" role="status" data-testid="thread-state-banner">
          <span>
            {locked
              ? 'Dieser Thread ist gesperrt. Nur Moderierende können hier schreiben.'
              : 'Dieser Thread ist archiviert.'}
          </span>
          {archived && access.canArchive && (
            <button className="btn btn-sm" disabled={busy} onClick={() => void patch({ archived: false }, 'Archivierung aufgehoben')} data-testid="thread-unarchive">
              Wiederherstellen
            </button>
          )}
        </div>
      )}

      <div className="thread-panel-body">
        <MessagePane
          key={thread.id}
          channelId={thread.id}
          variant="panel"
          jumpToMessageId={jumpId}
          onJumped={() => {
            const next = new URLSearchParams(params);
            next.delete('message');
            setParams(next, { replace: true });
          }}
        />
      </div>

      {menu && <Menu anchor={menu} items={menuItems} onClose={() => setMenu(null)} testId="thread-menu" placement="bottom-end" />}
      {dialog?.kind === 'rename' && (
        <RenameDialog
          initial={name}
          title={inForum ? 'Titel ändern' : 'Thread umbenennen'}
          onClose={() => setDialog(null)}
          onSave={async (value) => {
            if (await patch({ name: value })) setDialog(null);
          }}
        />
      )}
      {dialog?.kind === 'tags' && parent && (
        <TagsDialog
          thread={thread}
          forum={parent}
          isMod={access.isMod}
          onClose={() => setDialog(null)}
          onSave={async (ids) => {
            if (await patch({ appliedTagIds: ids }, 'Tags gespeichert')) setDialog(null);
          }}
        />
      )}
      {dialog?.kind === 'delete' && (
        <ConfirmDialog
          title={inForum ? 'Beitrag löschen' : 'Thread löschen'}
          message={`„${name}" und alle Nachrichten darin werden gelöscht. Das lässt sich nicht rückgängig machen.`}
          confirmLabel="Löschen"
          danger
          busy={busy}
          onConfirm={() => void remove()}
          onClose={() => setDialog(null)}
        />
      )}
    </aside>
  );
}

function RenameDialog({ initial, title, onSave, onClose }: { initial: string; title: string; onSave: (v: string) => Promise<void>; onClose: () => void }) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const trimmed = value.trim();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmed || trimmed === initial) return onClose();
    setBusy(true);
    await onSave(trimmed);
    setBusy(false);
  };
  return (
    <Modal title={title} onClose={onClose} testId="thread-rename-dialog">
      <form className="stack" onSubmit={(e) => void submit(e)}>
        <div className="field">
          <label htmlFor="thread-rename-input">Name</label>
          <input id="thread-rename-input" className="input" value={value} maxLength={100} onChange={(e) => setValue(e.target.value)} autoFocus data-testid="thread-rename-input" />
        </div>
        <div className="row thread-dialog-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !trimmed} data-testid="thread-rename-save">
            Speichern
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TagsDialog({
  thread,
  forum,
  isMod,
  onSave,
  onClose,
}: {
  thread: ChannelDto;
  forum: ChannelDto;
  isMod: boolean;
  onSave: (ids: string[]) => Promise<void>;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(thread.thread?.appliedTagIds ?? []);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title="Tags bearbeiten" onClose={onClose} testId="thread-tags-dialog">
      <div className="stack">
        <TagPicker tags={forum.availableTags ?? []} selected={selected} onChange={setSelected} isMod={isMod} initial={thread.thread?.appliedTagIds ?? []} />
        <div className="row thread-dialog-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void onSave(selected).finally(() => setBusy(false));
            }}
            data-testid="thread-tags-save"
          >
            Speichern
          </button>
        </div>
      </div>
    </Modal>
  );
}
